



























import {
  computed, defineComponent, PropType, ref, watch,
} from '@vue/composition-api';
import { Form, ValidateError } from '@hb/finance-ui';
import { Toast, Dialog } from '@hb/coffee';

import FormItem from '@/widgets/ocr-identity/components/user-info-form/FormItem.vue';
import { validatorPersonName } from '@/shared/utils/validator';
import { idNoMaskFormart, nameMaskFormat } from '@/shared/utils/dataMaskFormat';
import { trimAll } from '@/shared/utils/trim';
import UserPersonalInfoApi from '@/api/userPersonalInfo';
import BioApi from '@/api/bio';
import { platformIdentityManager } from '@/shared/identity-verification';
import { BioAuthVo } from '@/vo/bioAuth';
import { UploadTypeEnum, UploadProcessTypeEnum, OcrProcessSourceEnum } from '@/shared/tools/ocr/enum';
import {
  TE_OCR_NEXT_FRONT_CHECK_SUCCEED,
  TE_OCR_NEXT_FRONT_CHECK_FAILED,
  TE_OCR_NEXT_BASE_CHECK_FAILED,
  ubtExposeNewOcr,
} from '@/shared/tools/ocr/tianqi';
import { MaskDataType } from '../../types';

export default defineComponent({
  name: 'userInfoForm',
  components: {
    Form,
    FormItem,
  },
  props: {
    identity: {
      type: Object,
      default: {},
    },
    maskData: {
      type: Object,
      default: {},
    },
    uploadType: {
      default: UploadTypeEnum.SDKCamera,
    },
    processSource: {
      type: Number as PropType<OcrProcessSourceEnum>,
      default: OcrProcessSourceEnum.OTHER,
    },
    ocrAfter: {
      type: Boolean,
      default: false,
    },
    ocrAfterLoan: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['changeIdentity', 'resolveProcessVerify'],
  setup(props, context) {
    const formRef = ref();
    const identity = computed(() => props.identity);
    /** 外部传入 - 身份二要素脱敏数据 */
    const maskData = computed(() => props.maskData as MaskDataType);
    /** 内部维护 - 身份二要素脱敏数据 */
    const currentMaskData = ref<MaskDataType>({});
    const shouldMaskUserName = computed(() => currentMaskData.value.shouldMaskUserName);

    /** 有正反面照片即展示提示信息 */
    const hadTips = computed(() => identity.value.idCardFrontUrl && identity.value.idCardBackUrl);

    const toastWithExpose = (message: string) => {
      ubtExposeNewOcr(TE_OCR_NEXT_FRONT_CHECK_FAILED, {
        uploadType: props.uploadType,
        processType: UploadProcessTypeEnum.SCAN,
        errorMessage: message,
      });

      Toast(message);
    };

    const onSubmit = async (errors: ValidateError[]) => {
      if (errors.length) {
        toastWithExpose(errors[0].message);
        return;
      }

      const { checkOcrNameRes } = await UserPersonalInfoApi.checkUserContact({
        ocrName: shouldMaskUserName.value
          ? currentMaskData.value.defaultUserName : identity.value.userName,
      }) || {};
      if (checkOcrNameRes) {
        toastWithExpose('身份证姓名与联系人姓名一致，请返回上一步更新联系人姓名');
        return;
      }

      ubtExposeNewOcr(TE_OCR_NEXT_FRONT_CHECK_SUCCEED, {
        uploadType: props.uploadType,
        processType: UploadProcessTypeEnum.SCAN,
      });

      try {
        const { shouldMaskIdCardNo } = currentMaskData.value;
        await BioApi.uploadIdentity2({
          ...identity.value,
          idCardBackImg: '',
          idCardFrontImg: '',
          idCardBackBase64: '',
          idCardFrontBase64: '',
          userName: shouldMaskUserName.value
            ? currentMaskData.value.defaultUserName : identity.value.userName,
          idCardNo: shouldMaskIdCardNo
            ? currentMaskData.value.defaultIdCardNo : identity.value.idCardNo,
          uploadType: props.uploadType,
          processSource: props.processSource,
          ocrAfter: props.ocrAfter,
          ocrAfterLoan: props.ocrAfterLoan,
        } as BioAuthVo.Identity, platformIdentityManager.dispatchNewUserStatus);
        /** 极简流程-successCb */
        context.emit('resolveProcessVerify');
      } catch (error: any) {
        ubtExposeNewOcr(TE_OCR_NEXT_BASE_CHECK_FAILED, {
          uploadType: props.uploadType,
          processType: UploadProcessTypeEnum.SCAN,
          errorCode: 'code' in error ? error.code : '',
          errorMessage: 'message' in error ? error.message : '',
        });

        // 身份证即将过期
        if (Number(error.code) === 40001) {
          // 清除接口异常Toast，否则Toast会盖在Dialog上面
          Toast.clear();
          Dialog.alert({
            confirmButtonText: '知道了',
            title: '身份证(即将)过期，请更换后重新上传',
            closeable: false,
            zIndex: 3000,
          }).then(() => {
            // 关闭弹窗
          });
        }
      }
    };

    const submitForm = async () => {
      if (formRef.value) {
        formRef.value.submitForm();
      }
    };

    /**  如果脱敏了则 姓名不需要验证 */
    const validateUserName = (
      userName: string,
    ): boolean => shouldMaskUserName.value || validatorPersonName(userName);

    const nameFormat = (userName: string): string => {
      if (shouldMaskUserName.value) {
        const nameMask = nameMaskFormat(userName);
        context.emit('changeIdentity', {
          ...identity.value,
          userName: nameMask,
        });
        return nameMask;
      }
      return trimAll(userName);
    };

    const idNoMaskFormartFn = (idCardNo: string): string => {
      const { shouldMaskIdCardNo } = currentMaskData.value;
      if (shouldMaskIdCardNo) {
        const idNoMask = idNoMaskFormart(idCardNo);
        context.emit('changeIdentity', {
          ...identity.value,
          idCardNo: idNoMask,
        });
        return idNoMask;
      }
      return idCardNo;
    };

    const onUserNameChange = (userName: string) => {
      const { defaultUserName = '' } = currentMaskData.value;
      if (shouldMaskUserName.value && userName.length < defaultUserName.length) {
        currentMaskData.value.shouldMaskUserName = false;
        context.emit('changeIdentity', {
          ...identity.value,
          userName: '',
        });
      } else {
        context.emit('changeIdentity', {
          ...identity.value,
          userName,
        });
      }
      window.scrollTo(0, Math.max(document.documentElement.scrollTop - 1, 0));
    };

    /** 部分身份证上会写的过期时间是“长期”，后端约定使用4102329600000来表示 */
    const getPeriodDate = (periodName: 'periodBegin' | 'periodEnd') => {
      const periodTime = identity.value[periodName];
      const isLongTerm = String(periodTime) === '4102329600000';

      if (periodTime) {
        return isLongTerm
          ? '长期'
          : new Date(Number(periodTime)).toLocaleDateString().replace(/\//g, '.');
      }

      return '';
    };

    /**  获取attrs */
    const getfieldAttrs = (name: string, label: string) => {
      const fieldProps: Dictionary<Dictionary> = {
        attrs: {
          name,
          label,
          readonly: true,
          value: identity.value[name],
          style: 'display: \'flex\'',
        },
        on: {},
      };

      switch (name) {
        case 'userName':
          fieldProps.attrs.readonly = false;
          fieldProps.attrs.maxLength = 25;
          fieldProps.attrs.rules = [
            { required: true, message: '请填写姓名' },
            {
              validator: validateUserName,
              message: '姓名只能包含2～25个汉字',
            },
          ];
          fieldProps.on.input = onUserNameChange;
          fieldProps.attrs.formatter = nameFormat;
          break;
        case 'idCardNo':
          fieldProps.attrs.formatter = idNoMaskFormartFn;
          break;
        case 'periodBegin':
          fieldProps.attrs.value = `${getPeriodDate(
            'periodBegin',
          )} - ${getPeriodDate('periodEnd')}`;
          break;
        default:
          break;
      }
      return fieldProps;
    };

    const initMaskData = (mask: MaskDataType) => {
      const { shouldMaskIdCardNo } = mask;
      const { userName, idCardNo } = identity.value;
      if (shouldMaskUserName || shouldMaskIdCardNo) {
        context.emit('changeIdentity', {
          ...identity.value,
          userName: shouldMaskUserName.value ? nameMaskFormat(userName) : userName,
          idCardNo: shouldMaskIdCardNo ? idNoMaskFormart(idCardNo) : idCardNo,
        });
      }
    };

    watch(() => maskData.value, (newData) => {
      if (formRef.value) {
        formRef.value.validateAll();
      }
      if (Object.keys(newData).length) {
        initMaskData(maskData.value);
        // 内部的maskData需更新
        currentMaskData.value = maskData.value;
      } else {
        currentMaskData.value = {};
      }
    }, { immediate: true });


    return {
      identity,
      hadTips,
      formRef,

      getfieldAttrs,
      onSubmit,
      validateUserName,
      submitForm,
    };
  },
});

